import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { Provider } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: Provider, redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  refreshSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,
  isAuthenticated: false,
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    if (data.user && data.session) {
      set({ 
        user: data.user, 
        session: data.session,
        isAuthenticated: true 
      });
    }
  },
  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    
    if (data.user && data.session) {
      set({ 
        user: data.user, 
        session: data.session,
        isAuthenticated: true 
      });
    }
  },
  signInWithProvider: async (provider: Provider, redirectTo?: string) => {
    // Construct the redirect URL based on current location and invitation params
    const currentUrl = new URL(window.location.href);
    const invitationId = currentUrl.searchParams.get('invitation');
    
    // Use environment variable for redirect URL, fallback to current origin
    const baseRedirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`;
    let redirectUrl = baseRedirectUrl;
    
    // If there's an invitation, add it to the callback URL
    if (invitationId) {
      redirectUrl += `?invitation=${invitationId}`;
    }
    
    // If a custom redirect is provided, use that instead
    if (redirectTo) {
      redirectUrl = redirectTo;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    
    if (error) throw error;
    
    // Note: OAuth redirect will happen automatically, so we don't set state here
    // The state will be set when the user returns from the OAuth provider
  },
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    set({ user: null, session: null, isAuthenticated: false });
  },
  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
  refreshSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session) {
        set({ 
          user: session.user,
          session: session,
          isAuthenticated: true
        });
      } else {
        set({ 
          user: null,
          session: null,
          isAuthenticated: false
        });
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      set({ 
        user: null,
        session: null,
        isAuthenticated: false
      });
    }
  }
}));